import React, { useRef, useEffect, useMemo, useState } from 'react';
import ReactECharts from 'echarts-for-react';
import * as echarts from 'echarts';
import moment from 'moment';
import { SelectBox } from 'devextreme-react';
import AssetsService from '../../../api/assets.service';
import { LoadPanel as LoadingPanel2 } from 'devextreme-react/load-panel';
import './mechanical.css';
import FeaturesService from 'api/features.service';
import { useTheme } from 'contexts/theme';

export default function SyncMechanicalCharts({ assetIds, startDate, endDate, features, assetColorMap = {} }) {
  const [loading, setLoading] = useState(false);
  const tempChartRef = useRef(null);
  const impactChartRef = useRef(null);
  const impactSpaceChartRef = useRef(null);
  // One per entry in cycleChartFields.
  const chartRefs = [useRef(null), useRef(null)];
  const [temperatureData, setTemperatureData] = useState([]);
  const [impactData, setImpactData] = useState([]);
  const [eventData, setCyclesData] = useState([]);
  const [grouping, setGrouping] = useState('Monthly');
  // Reading the unit preference must not be able to take the page down -- the
  // entry can be missing or unparseable (logged out, cleared site data).
  const isFahrenheit = useMemo(() => {
    let storedUser = null;
    try {
      storedUser = JSON.parse(localStorage.getItem('user'));
    } catch (err) {
      console.error('Could not read the stored user', err);
    }
    return !storedUser?.temperatureUnitMetric;
  }, []);
  const tempUnit = isFahrenheit ? '°F' : '°C';
  const [isCycleTelemetryVisible, setIsCycleTelemetryVisible] = useState(false);
  const [isTemperatureTelemetryVisible, setIsTemperatureTelemetryVisible] = useState(false);
  const [isImpactTelemetryVisible, setIsImpactTelemetryVisible] = useState(false);
  const { theme } = useTheme();
  const axisTextColor = theme === 'dark' ? 'rgba(255,255,255,0.87)' : 'rgba(0,0,0,0.87)';
  const noDataColor = theme === 'dark' ? '#bbb' : '#999';
  // Subtle horizontal gridlines: echarts' default splitLine is a light grey
  // that reads as near-white on the dark background and drowns out the data.
  const gridLineColor = theme === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.1)';

  // Process temperature data based on the unit
  const displayTempData = useMemo(() => {
    const sorted = [...temperatureData].sort((a, b) => new Date(a.time) - new Date(b.time));
    if (!isFahrenheit) return sorted;

    return sorted.map((item) => ({
      ...item,
      // A missing reading stays missing -- echarts draws null as a gap, whereas
      // converting it lands it on the chart as 32 degrees.
      temperature:
        item.temperature == null ? null : (item.temperature * 1.8 + 32).toFixed(2),
    }));
  }, [temperatureData, isFahrenheit]);


  useEffect(() => {
    const checkVisibility = async () => {
      // 1. Define the configuration for the features you want to check
      const config = [
        { key: 'cycleTelemetry', id: 4, setter: setIsCycleTelemetryVisible },
        { key: 'temperatureTelemetry', id: 3, setter: setIsTemperatureTelemetryVisible },
        { key: 'impactTelemetry', id: 6, setter: setIsImpactTelemetryVisible },
      ];

      for (const { key, id, setter } of config) {
        const feature = features?.[key];

        // Case 1: Feature is off entirely, or not configured yet
        if (!feature?.isfeature) {
          setter(false);
          continue;
        }

        // Case 2: Feature is on and NOT device-specific (Show it immediately)
        if (!feature.isDeviceFeature) {
          setter(true);
          continue;
        }

        // Case 3: Feature is device-specific. Check all asset IDs.
        try {
          const requests = assetIds.map((assetId) => FeaturesService.getDeviceFeature(assetId, id));
          const responses = await Promise.all(requests);

          // Every device must have the feature for it to be visible
          const allDevicesHaveFeature = responses.every((res) => res.data.data.has_feature);
          setter(allDevicesHaveFeature);
        } catch (err) {
          console.error(`Failed to fetch Device Feature for ${key}`, err);
          setter(false); // Hide on error as a safety fallback
        }
      }

      // Trigger data fetch if dates are present
      if (startDate && endDate) {
        getData();
      }
    };

    checkVisibility();
  }, [startDate, endDate, features, assetIds]); // Added missing dependencies

  async function getData() {
    setLoading(true);
    try {
      const from = startDate;
      const to = endDate;

      // 2. Clean Await usage
      const response = await AssetsService.locationHistoryMechanical(assetIds, from, to);
      const result = response?.data?.data;

      if (!result) {
        // setLoadingText('No Data');
        return;
      }
      if (result.impactData) {
        setImpactData(result.impactData);
      }
      // Always set temperature if it exists to avoid repeating it in every 'if' block
      if (result.temperatureData) {
        setTemperatureData(result.temperatureData);
      }
      if (result.cyclesData?.length) {
        setCyclesData(result.cyclesData);
      } else if (!result.temperatureData?.length) {
        // If no history, no cycles, and no temperature
        // setCyclesData([]);
        // setLoadingText('No Data');
      }
    } catch (error) {
      console.error('Error fetching mechanical data:', error);
      // setLoadingText('Error loading data');
    } finally {
      setLoading(false); // 2. Stop loading regardless of success/error
    }
  }

  const temperatureDataByAsset = useMemo(() => {
    if (!displayTempData || displayTempData.length === 0) return {};

    const byAsset = {};
    displayTempData.forEach((item) => {
      const assetName = item.name;
      if (!byAsset[assetName]) byAsset[assetName] = [];
      byAsset[assetName].push(item);
    });

    // Sort each asset's data by time
    Object.values(byAsset).forEach((arr) =>
      arr.sort((a, b) => new Date(a.time) - new Date(b.time))
    );

    return byAsset;
  }, [displayTempData]);

  const processedDataByAsset = useMemo(() => {
    if (!eventData || eventData.length === 0) return {};

    const assetBins = {};

    eventData.forEach((item) => {
      const assetId = item.name; // <-- confirm this field name
      if (!assetBins[assetId]) assetBins[assetId] = {};

      const m = moment(item.time);
      let binKey;
      if (grouping === 'Hourly') binKey = m.startOf('hour').format('YYYY-MM-DD HH:00:00');
      else if (grouping === 'Weekly') binKey = m.startOf('isoWeek').format('YYYY-MM-DD');
      else if (grouping === 'Monthly') binKey = m.startOf('month').format('YYYY-MM-DD');
      else binKey = m.startOf('day').format('YYYY-MM-DD');

      if (!assetBins[assetId][binKey]) {
        assetBins[assetId][binKey] = {
          time: binKey,
          shot_count: 0,
          cycle_period_sum: 0,
          cycle_period_count: 0,
          shot_count_total: 0,
        };
      }

      const bin = assetBins[assetId][binKey];
      bin.shot_count += Number(item.shot_count || 0);
      bin.shot_count_total = Math.max(bin.shot_count_total, Number(item.shot_count_total || 0));
      if (item.cycle_period > 0) {
        bin.cycle_period_sum += Number(item.cycle_period);
        bin.cycle_period_count += 1;
      }
    });

    const result = {};
    Object.entries(assetBins).forEach(([assetId, bins]) => {
      result[assetId] = Object.values(bins)
        .map((b) => ({
          ...b,
          cycle_period:
            b.cycle_period_count > 0 ? (b.cycle_period_sum / b.cycle_period_count).toFixed(2) : 0,
        }))
        .sort((a, b) => new Date(a.time) - new Date(b.time));
    });

    return result;
  }, [eventData, grouping]);

  // All bin keys across every asset -- the category axis of the cycle charts.
  const allTimes = useMemo(
    () =>
      [
        ...new Set(Object.values(processedDataByAsset).flatMap((bins) => bins.map((b) => b.time))),
      ].sort(),
    [processedDataByAsset]
  );

  // ---------------------------------------------------------------------------
  // Synchronized zooming
  // ---------------------------------------------------------------------------
  // Every chart plots the same x axis -- the selected date period -- but not with
  // the same axis type: the telemetry charts use a `time` axis while the cycle
  // charts use a `category` axis of binned timestamps. echarts' group connect
  // only forwards the raw zoom payload, so a percentage taken off a time axis
  // lands on the wrong bins of a category axis. Instead we keep one absolute
  // [start, end] millisecond window and translate it into whatever each chart's
  // axis understands.
  const periodStart = startDate ? moment(startDate).valueOf() : null;
  const periodEnd = endDate ? moment(endDate).valueOf() : null;

  // Pin every time axis to the selected period so the same pixel is the same
  // instant on all of them.
  const timeAxisBounds =
    periodStart != null && periodEnd != null ? { min: periodStart, max: periodEnd } : {};

  // The window every chart is currently showing. null = the full period.
  const zoomWindowRef = useRef(null);
  // Guards against the dataZoom events our own dispatchAction calls trigger.
  const isSyncingRef = useRef(false);
  // Event handlers can close over an older render, so read these through refs.
  const allTimesRef = useRef(allTimes);
  const periodRef = useRef(null);
  allTimesRef.current = allTimes;
  periodRef.current = { start: periodStart, end: periodEnd };

  // Changing the date period invalidates the window -- reset it while rendering
  // so the fresh options are built unzoomed.
  const periodKeyRef = useRef(null);
  const periodKey = `${periodStart}_${periodEnd}`;
  if (periodKeyRef.current !== periodKey) {
    periodKeyRef.current = periodKey;
    zoomWindowRef.current = null;
  }

  const getSyncTargets = () =>
    [
      { ref: impactChartRef, axis: 'time' },
      { ref: impactSpaceChartRef, axis: 'time' },
      { ref: tempChartRef, axis: 'time' },
      ...chartRefs.map((ref) => ({ ref, axis: 'category' })),
    ]
      .map((t) => ({ ...t, chart: t.ref.current?.getEchartsInstance() }))
      .filter((t) => t.chart && !t.chart.isDisposed());

  // A bin index -> the instant that bin starts at.
  const indexToTime = (index) => {
    const times = allTimesRef.current;
    if (!times.length) return null;
    const i = Math.max(0, Math.min(times.length - 1, Math.round(index)));
    return moment(times[i]).valueOf();
  };

  // An instant -> the bin that contains it. `edge` decides which way to round so
  // the window always covers the requested range rather than cutting into it.
  const timeToIndex = (ms, edge) => {
    const values = allTimesRef.current.map((t) => moment(t).valueOf());
    if (!values.length) return 0;
    if (edge === 'start') {
      let i = 0;
      while (i + 1 < values.length && values[i + 1] <= ms) i += 1;
      return i;
    }
    let i = values.length - 1;
    while (i > 0 && values[i - 1] >= ms) i -= 1;
    return i;
  };

  // Read the window a chart is currently showing, as absolute milliseconds.
  const readWindow = (target) => {
    const dz = target.chart.getOption()?.dataZoom?.[0];
    if (!dz) return null;

    if (target.axis === 'category') {
      const times = allTimesRef.current;
      if (!times.length) return null;
      const lastIdx = times.length - 1;
      const toIndex = (value, percent) => {
        if (typeof value === 'number') return value;
        if (typeof value === 'string' && times.indexOf(value) !== -1) return times.indexOf(value);
        return Math.round((percent / 100) * lastIdx);
      };
      const start = indexToTime(toIndex(dz.startValue, dz.start ?? 0));
      const end = indexToTime(toIndex(dz.endValue, dz.end ?? 100));
      return start == null || end == null ? null : { start, end };
    }

    // On a time axis echarts writes the zoomed window back as startValue /
    // endValue, which is exactly the absolute range we want.
    const toMs = (v) => {
      if (typeof v === 'number') return v;
      if (v == null) return null;
      const ms = moment(v).valueOf();
      return isNaN(ms) ? null : ms;
    };
    const startValue = toMs(dz.startValue);
    const endValue = toMs(dz.endValue);
    if (startValue != null && endValue != null) return { start: startValue, end: endValue };

    // Fallback for the very first zoom, before echarts has filled those in:
    // map the percentages onto the selected period.
    const { start: pStart, end: pEnd } = periodRef.current;
    if (pStart == null || pEnd == null) return null;
    const span = pEnd - pStart;
    return {
      start: pStart + (span * (dz.start ?? 0)) / 100,
      end: pStart + (span * (dz.end ?? 100)) / 100,
    };
  };

  const applyWindow = (target, range) => {
    target.chart.dispatchAction(
      target.axis === 'category'
        ? {
            type: 'dataZoom',
            startValue: timeToIndex(range.start, 'start'),
            endValue: timeToIndex(range.end, 'end'),
          }
        : { type: 'dataZoom', startValue: range.start, endValue: range.end }
    );
  };

  // Bake the shared window into an option, so the re-render that follows any
  // state change (notMerge rebuilds the chart from scratch) keeps the zoom.
  const getDataZoom = (axisType) => {
    const range = zoomWindowRef.current;
    let bounds = {};
    if (range) {
      bounds =
        axisType === 'category'
          ? {
              startValue: timeToIndex(range.start, 'start'),
              endValue: timeToIndex(range.end, 'end'),
            }
          : { startValue: range.start, endValue: range.end };
    }
    return [
      { show: true, realtime: true, xAxisIndex: [0], ...bounds },
      { type: 'inside', realtime: true, xAxisIndex: [0], ...bounds },
    ];
  };

  const handleZoom = (sourceRef) => () => {
    if (isSyncingRef.current) return;
    const targets = getSyncTargets();
    const source = targets.find((t) => t.ref === sourceRef);
    if (!source) return;

    const range = readWindow(source);
    if (!range || !(range.end > range.start)) return;

    // Nothing moved -- stop here so an echoed event can never bounce between
    // charts. Category charts can only resolve to a bin edge, so allow slack.
    const current = zoomWindowRef.current;
    if (
      current &&
      Math.abs(current.start - range.start) < 1000 &&
      Math.abs(current.end - range.end) < 1000
    ) {
      return;
    }
    zoomWindowRef.current = range;

    isSyncingRef.current = true;
    try {
      targets.filter((t) => t !== source).forEach((t) => applyWindow(t, range));
    } finally {
      isSyncingRef.current = false;
    }
  };

  // The toolbox reset on one chart clears the zoom on all of them.
  const handleRestore = () => {
    zoomWindowRef.current = null;
    isSyncingRef.current = true;
    try {
      getSyncTargets().forEach(({ chart }) =>
        chart.dispatchAction({ type: 'dataZoom', start: 0, end: 100 })
      );
    } finally {
      isSyncingRef.current = false;
    }
  };

  // ---------------------------------------------------------------------------
  // Synchronized crosshair
  // ---------------------------------------------------------------------------
  // Hovering one chart draws the axis pointer on every other chart at the same
  // instant, so a spike in one can be read against the others at a glance. Same
  // translation problem as the zoom -- the hovered position is turned into a
  // timestamp and resolved again against each chart's own axis. It goes via
  // pixels because `updateAxisPointer` only acts on a point the target's
  // coordinate system actually contains.
  const isPointerSyncingRef = useRef(false);
  // The instant we last broadcast, so a chart echoing its own snapped value
  // back at us settles instead of bouncing.
  const lastPointerMsRef = useRef(null);
  const noop = () => {};

  // The bin nearest an instant, for the category charts.
  const timeToNearestIndex = (ms) => {
    const values = allTimesRef.current.map((t) => moment(t).valueOf());
    if (!values.length) return null;
    let best = 0;
    for (let i = 1; i < values.length; i += 1) {
      if (Math.abs(values[i] - ms) < Math.abs(values[best] - ms)) best = i;
    }
    return best;
  };

  // A y inside the chart's plotting area -- an x on its own is not a point the
  // grid can contain. Probing outwards from the middle finds one in a step or
  // two whatever the chart's height and margins are.
  const findGridY = (chart, x) => {
    const height = chart.getHeight();
    const fractions = [0.5, 0.4, 0.6, 0.3, 0.7, 0.2, 0.8];
    for (let i = 0; i < fractions.length; i += 1) {
      const y = height * fractions[i];
      if (chart.containPixel({ gridIndex: 0 }, [x, y])) return y;
    }
    return null;
  };

  const showPointerAt = (target, ms) => {
    const axisValue = target.axis === 'category' ? timeToNearestIndex(ms) : ms;
    if (axisValue == null) return;
    const x = target.chart.convertToPixel({ xAxisIndex: 0 }, axisValue);
    if (typeof x !== 'number' || isNaN(x)) return;
    // Outside this chart's axis range -- nothing to point at.
    const y = findGridY(target.chart, x);
    if (y == null) return;
    target.chart.dispatchAction({
      type: 'updateAxisPointer',
      currTrigger: 'mousemove',
      x,
      y,
      // echarts routes the follow-up tooltip and series highlight through this
      // hook, so a no-op leaves just the line on the charts we are not hovering.
      dispatchAction: noop,
    });
  };

  const hidePointer = (target) => {
    target.chart.dispatchAction({
      type: 'updateAxisPointer',
      currTrigger: 'leave',
      dispatchAction: noop,
    });
  };

  const handleAxisPointer = (sourceRef) => (params) => {
    if (isPointerSyncingRef.current) return;
    const targets = getSyncTargets();
    const source = targets.find((t) => t.ref === sourceRef);
    if (!source) return;
    const peers = targets.filter((t) => t !== source);

    // A category axis reports the hovered bin index, a time axis the timestamp.
    const hovered = (params?.axesInfo || []).find((a) => a.axisDim === 'x');
    let ms = null;
    if (hovered?.value != null) {
      ms = source.axis === 'category' ? indexToTime(hovered.value) : Number(hovered.value);
    }

    if (ms != null && !isNaN(ms) && ms === lastPointerMsRef.current) return;
    lastPointerMsRef.current = ms;

    isPointerSyncingRef.current = true;
    try {
      if (ms == null || isNaN(ms)) peers.forEach(hidePointer);
      else peers.forEach((t) => showPointerAt(t, ms));
    } finally {
      isPointerSyncingRef.current = false;
    }
  };

  // Leaving the charts altogether clears every pointer.
  const handlePointerLeave = () => {
    if (isPointerSyncingRef.current) return;
    lastPointerMsRef.current = null;
    isPointerSyncingRef.current = true;
    try {
      getSyncTargets().forEach(hidePointer);
    } finally {
      isPointerSyncingRef.current = false;
    }
  };

  const syncEvents = (sourceRef) => ({
    dataZoom: handleZoom(sourceRef),
    restore: handleRestore,
    updateAxisPointer: handleAxisPointer(sourceRef),
    globalout: handlePointerLeave,
  });

  // Tick labels for the time axes. echarts calls a label formatter more than
  // once per layout (measuring, then drawing, then again on every zoom), so the
  // label cannot depend on what the previous call saw -- the old version kept a
  // `lastDate` across calls and dropped the date markers from the second pass
  // on. The rule is positional instead: the first tick anchors the date, and
  // every midnight marks the day rolling over.
  const formatTimeAxisLabel = (value, index) => {
    const m = moment(value);
    const dateStr = m.format('MMM DD');
    const timeStr = m.format('HH:mm');
    if (timeStr === '00:00') return dateStr;
    return index === 0 ? `${dateStr}\n${timeStr}` : timeStr;
  };

  // Temperature Chart Option (Raw Data, no binning)
  const getTempOption = () => {
    const assetEntries = Object.entries(temperatureDataByAsset);
    const hasData = assetEntries.some(([, data]) => data.length > 0);
    const hasHumidity = assetEntries.some(([, data]) => data.some((d) => d.humidity != null));

    const tempSeries = assetEntries.map(([assetName, data]) => ({
      name: assetName,
      type: 'line',
      yAxisIndex: 0,
      data: data.map((d) => [d.time, d.temperature]),
      lineStyle: { width: 2, color: assetColorMap[assetName] ?? '#ccc' },
      itemStyle: { color: assetColorMap[assetName] ?? '#ccc' },
      showSymbol: false,
      sampling: 'lttb',
    }));

    const humiditySeries = hasHumidity
      ? assetEntries
          .map(([assetName, data]) => {
            const pts = data.filter((d) => d.humidity != null);
            if (pts.length === 0) return null;
            const baseColor = assetColorMap[assetName] ?? '#ccc';
            return {
              name: `${assetName} Humidity`,
              type: 'line',
              yAxisIndex: 1,
              data: pts.map((d) => [d.time, d.humidity]),
              lineStyle: { width: 2, color: baseColor, type: 'dashed', opacity: 0.7 },
              itemStyle: { color: baseColor },
              showSymbol: false,
              sampling: 'lttb',
            };
          })
          .filter(Boolean)
      : [];

    const yAxis = hasHumidity
      ? [
          { type: 'value', name: `Temperature [${tempUnit}]`, scale: true, axisLabel: { color: axisTextColor }, axisLine: { lineStyle: { color: axisTextColor } }, nameTextStyle: { color: axisTextColor }, splitLine: { lineStyle: { color: gridLineColor } } },
          {
            type: 'value',
            name: 'Humidity [%]',
            scale: true,
            position: 'right',
            axisLine: { show: true, lineStyle: { color: axisTextColor } },
            axisLabel: { formatter: '{value}%', color: axisTextColor },
            nameTextStyle: { padding: [0, 0, 0, -90], color: axisTextColor },
            splitLine: { show: false },
          },
        ]
      : { type: 'value', name: `Temperature [${tempUnit}]`, scale: true, axisLabel: { color: axisTextColor }, axisLine: { lineStyle: { color: axisTextColor } }, nameTextStyle: { color: axisTextColor }, splitLine: { lineStyle: { color: gridLineColor } } };

    return {
      title: { text: 'Temperature', left: 'center', textStyle: { fontSize: 14, color: axisTextColor }, top: 4 },
      graphic: !hasData
        ? {
            type: 'text',
            left: 'center',
            top: 'middle',
            style: { text: 'No Data Available', fill: noDataColor, font: '14px sans-serif' },
          }
        : [],
      toolbox: {
        right: 10,
        feature: { restore: { title: 'Reset' }, saveAsImage: { title: 'Save', name: 'Telemetry' } },
      },
      grid: { left: 60, right: hasHumidity ? 70 : 30, top: 40, bottom: 100 },
      legend: {
        bottom: 55,
        type: 'scroll',
        textStyle: { color: axisTextColor },
        formatter: (name) => {
          if (name.endsWith(' Humidity')) return `${name} (Humidity %)`;
          return `${name} (Temperature ${tempUnit})`;
        },
      },
      tooltip: {
        trigger: 'axis',
        formatter: (params) => {
          const timeHeader = moment(params[0].axisValue).format('MMM DD HH:mm');
          let res = `<b>${timeHeader}</b><br/>`;
          params.forEach((item) => {
            if (item.seriesName.endsWith(' Humidity')) {
              res += `${item.marker} ${item.seriesName}: <b>${item.value[1]}%</b><br/>`;
            } else {
              res += `${item.marker} ${item.seriesName}: <b>${item.value[1]} ${tempUnit}</b><br/>`;
            }
          });
          return res;
        },
      },
      xAxis: {
        type: 'time',
        ...timeAxisBounds,
        axisLabel: {
          hideOverlap: true,
          color: axisTextColor,
          formatter: formatTimeAxisLabel,
        },
        axisLine: { lineStyle: { color: axisTextColor } },
        splitNumber: 6,
      },
      yAxis,
      dataZoom: getDataZoom('time'),
      series: [...tempSeries, ...humiditySeries],
    };
  };

  const getImpactOption = () => {
    const hasData = impactData && impactData.length > 0;
    return {
      title: { text: 'Impact', left: 'center', textStyle: { fontSize: 14, color: axisTextColor }, top: 4 },
      graphic: !hasData
        ? {
            type: 'text',
            left: 'center',
            top: 'middle',
            style: {
              text: 'No Data Available',
              fill: noDataColor,
              font: '14px sans-serif',
            },
          }
        : [],
      toolbox: {
        right: 10,
        feature: {
          restore: { title: 'Reset' },
          saveAsImage: {
            title: 'Save',
            name: 'Telemetry',
          },
        },
      },
      grid: { left: 60, right: 30, top: 40, bottom: 75 },
      tooltip: {
        trigger: 'axis',
        formatter: (params) => {
          // 1. Get the time from the first item in the axis stack
          const timeHeader = moment(params[0].axisValue).format('MMM DD HH:mm:ss');

          let res = `<b>${timeHeader}</b><br/>`;

          params.forEach((item) => {
            // 2. Access max_g from the value object
            const val = item.value.max_g;

            // 3. Display marker and value with 'mG' unit
            if (val !== undefined) {
              res += `${item.marker} Impact: <b>${val} mG</b>`;
            }
          });

          return res;
        },
      },
      dataset: {
        source: [...impactData].sort((a, b) => new Date(a.time) - new Date(b.time)),
      },
      xAxis: {
        type: 'time',
        ...timeAxisBounds,
        axisLabel: {
          hideOverlap: true,
          color: axisTextColor,
          formatter: formatTimeAxisLabel,
        },
        axisLine: { lineStyle: { color: axisTextColor } },
        // Optional: splitNumber helps ECharts decide how many ticks to try and draw
        splitNumber: 6,
      },
      yAxis: { type: 'value', name: `Force [mG]`, scale: true, axisLabel: { color: axisTextColor }, axisLine: { lineStyle: { color: axisTextColor } }, nameTextStyle: { color: axisTextColor }, splitLine: { lineStyle: { color: gridLineColor } } },
      dataZoom: getDataZoom('time'),
      series: [
        {
          // name: 'Temperature',
          type: 'line',
          encode: {
            x: 'time', // Matches the key for the X-axis
            y: 'max_g', // <--- CHANGE THIS to your data key (e.g., 'impact', 'temp')
          },
          lineStyle: { width: 2, color: '#FEC002' },
          itemStyle: { color: '#FEC002' },
          showSymbol: false,
          sampling: 'lttb',
          // --- Added professional areaStyle below ---
          // areaStyle: {
          //   color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
          //     {
          //       offset: 0,
          //       color: 'rgba(249, 221, 101, 0.4)', // Your yellow color with 40% opacity at top
          //     },
          //     {
          //       offset: 1,
          //       color: 'rgba(249, 221, 101, 0)', // Fades to completely transparent at bottom
          //     },
          //   ]),
          // },
          // ------------------------------------------
        },
      ],
    };
  };

  const stringToColor = (str) => {
    if (!str) return '#ccc';
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    let color = '#';
    for (let i = 0; i < 3; i++) {
      let value = (hash >> (i * 8)) & 0xff;
      color += ('00' + value.toString(16)).slice(-2);
    }
    return color;
  };

  const transformSpaceData = (rawData) => {
    const sorted = [...rawData].sort((a, b) => new Date(a.time) - new Date(b.time));

    return sorted
      .map((item, index) => {
        const startTime = new Date(item.time).getTime();
        const nextItem = sorted[index + 1];
        const endTime = nextItem ? new Date(nextItem.time).getTime() : startTime;

        return {
          // FIX: Set the name property to the joined space_name
          // If space_name is null (no match in DB), it falls back to space_id
          name: item.space_name || item.space_id,
          value: [0, startTime, endTime, item.max_g],
          itemStyle: {
            // Use the name for color generation so the color stays consistent with the label
            color: stringToColor(item.space_name || item.space_id),
          },
        };
      })
      .filter((item) => item.value[2] > 0);
  };

  const getSpaceOption = () => {
    const data = transformSpaceData(impactData);

    return {
      title: { text: 'Location', left: 'center', textStyle: { fontSize: 14, color: axisTextColor }, top: 4 },
      tooltip: {
        trigger: 'item',
        formatter: (params) => {
          const spaceName = params.name;

          // 3. Get the max_g value we stored at index 3
          const force = params.value[3];

          return `
            <div style="border-bottom: 1px solid rgba(255,255,255,.3); font-size: 14px; padding-bottom: 7px; margin-bottom: 7px">
              <b>${spaceName}</b>
            </div>
            <span style="color: #FEC002">●</span> Max Impact: <b>${force} mG</b><br/>
          `;
        },
      },
      grid: { left: 60, right: 30, top: 40, bottom: 75 },
      xAxis: {
        type: 'time',
        // Span the selected period, like the other time charts, so zooming one
        // chart lands on the same instant here.
        ...timeAxisBounds,
        // This chart's tooltip is per-item, so it has no axis pointer of its
        // own -- turn one on explicitly so the shared crosshair reaches it, but
        // leave its tooltip alone.
        axisPointer: { show: true, type: 'line', triggerTooltip: false, label: { show: false } },
        axisLabel: {
          hideOverlap: true,
          color: axisTextColor,
          formatter: formatTimeAxisLabel,
        },
        axisLine: { lineStyle: { color: axisTextColor } },
      },
      yAxis: { show: false, min: 0, max: 1 },
      dataZoom: getDataZoom('time'),
      series: [
        {
          type: 'custom',
          renderItem: (params, api) => {
            const start = api.coord([api.value(1), 0]);
            const end = api.coord([api.value(2), 0]);
            const height = 40;

            return {
              type: 'rect',
              shape: echarts.graphic.clipRectByRect(
                {
                  x: start[0],
                  y: start[1] - height / 2,
                  width: Math.max(end[0] - start[0], 1),
                  height: height,
                },
                {
                  x: params.coordSys.x,
                  y: params.coordSys.y,
                  width: params.coordSys.width,
                  height: params.coordSys.height,
                }
              ),
              style: api.style(),
            };
          },
          encode: { x: [1, 2], y: 0 },
          data: data,
        },
      ],
    };
  };

  // const ASSET_COLORS = ['#f9dd65', '#65b8f9', '#f965a0', '#65f9a0', '#f9a065'];

  const getOption = (yField) => {
    const assetEntries = Object.entries(processedDataByAsset);
    const hasData = assetEntries.some(([, bins]) => bins.length > 0);

    let ylabel, title;
    const chartType = yField === 'cycle_period' ? 'line' : 'bar';
    if (yField === 'shot_count') {
      ylabel = 'Cycles';
      title = 'Cycles';
    } else if (yField === 'cycle_period') {
      ylabel = 'Cycle Time [s]';
      title = 'Cycle Time';
    }

    const series = assetEntries.map(([assetId, bins], i) => {
      // Map bins into a lookup for quick access by time
      const binByTime = Object.fromEntries(bins.map((b) => [b.time, b]));

      return {
        type: chartType,
        name: assetId,
        // color: ASSET_COLORS[i % ASSET_COLORS.length],
        color: assetColorMap[assetId] ?? '#ccc',
        smooth: true,
        data: allTimes.map((t) => binByTime[t]?.[yField] ?? null),
        label: {
          show: false,
          position: 'top',
          distance: 10,
          color: axisTextColor,
          fontSize: 12,
          formatter: (params) => params.value ?? '',
        },
      };
    });

    return {
      title: { text: title, left: 'center', textStyle: { fontSize: 14, color: axisTextColor }, top: 4 },
      graphic: !hasData
        ? {
            type: 'text',
            left: 'center',
            top: 'middle',
            style: { text: 'No Data Available', fill: noDataColor, font: '14px sans-serif' },
          }
        : [],
      legend: { bottom: 55, type: 'scroll', textStyle: { color: axisTextColor } },
      toolbox: {
        right: 10,
        feature: { restore: { title: 'Reset' }, saveAsImage: { title: 'Save', name: 'Telemetry' } },
      },
      grid: { left: 60, right: 30, top: 40, bottom: 100 },
      tooltip: { trigger: 'axis' },
      xAxis: {
        type: 'category',
        data: allTimes,
        axisLabel: {
          color: axisTextColor,
          rich: {
            month: { fontWeight: 'bold', color: axisTextColor },
          },
          formatter: (val) => {
            if (grouping === 'Hourly') {
              const m = moment(val);
              return `${m.format('DD')} {month|${m.format('MMM')}} ${m.format('HH:mm')}`;
            }
            return moment(val).format('MMM DD');
          },
        },
        axisLine: { lineStyle: { color: axisTextColor } },
      },
      yAxis: { type: 'value', name: ylabel, scale: true, axisLabel: { color: axisTextColor }, axisLine: { lineStyle: { color: axisTextColor } }, nameTextStyle: { color: axisTextColor }, splitLine: { lineStyle: { color: gridLineColor } } },
      dataZoom: getDataZoom('category'),
      series,
    };
  };

  // A chart that mounts after the user has already zoomed (e.g. its feature flag
  // resolves late, or new data arrives) has to catch up with the shared window.
  useEffect(() => {
    const range = zoomWindowRef.current;
    if (!range) return;
    isSyncingRef.current = true;
    try {
      getSyncTargets().forEach((t) => applyWindow(t, range));
    } finally {
      isSyncingRef.current = false;
    }
  });

  // One chart per binned metric; getOption derives the title, axis label and
  // series type from the field. Order matches chartRefs.
  const cycleChartFields = ['shot_count', 'cycle_period'];

  const chartContainerStyle = {
    flex: 1,
    background: 'var(--app-surface)',
    padding: '0px',
    borderRadius: '8px',
    border: '1px solid var(--app-border)',
  };

  return (
    <>
      <div style={{ position: 'relative', height: '100%' }}>
        <LoadingPanel2
          shadingColor="rgba(0,0,0,0.4)"
          visible={loading}
          showIndicator={true}
          shading={true}
          showPane={true}
          hideOnOutsideClick={false}
        />
      </div>
      <div className="cycles-container">
        <div className="cycles-grouping-container">
          <SelectBox
            style={{ width: '130px' }}
            dataSource={['Hourly', 'Daily', 'Weekly', 'Monthly']}
            value={grouping}
            onValueChanged={(e) => setGrouping(e.value)}
            label="Grouping"
            stylingMode="outlined"
          />
        </div>
        <div
          style={{
            width: 'calc(100% - 150px)',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
          }}
        >
          {/* 1. Impact Chart - Now a direct child */}
          {isImpactTelemetryVisible && (
            <div style={chartContainerStyle}>
              <ReactECharts
                ref={impactChartRef}
                option={getImpactOption()}
                style={{ height: '100%', width: '100%' }}
                notMerge={true}
                onEvents={syncEvents(impactChartRef)}
              />
            </div>
          )}

          {/* 2. Space Timeline - Now a direct child */}
          {isImpactTelemetryVisible && (
            <div style={chartContainerStyle}>
              <ReactECharts
                ref={impactSpaceChartRef}
                option={getSpaceOption()}
                style={{ height: '100%', width: '100%' }}
                notMerge={true}
                onEvents={syncEvents(impactSpaceChartRef)}
              />
            </div>
          )}

          {/* 3. Temperature Chart */}
          {isTemperatureTelemetryVisible && (
            <div style={chartContainerStyle}>
              <ReactECharts
                ref={tempChartRef}
                option={getTempOption()}
                style={{ height: '100%', width: '100%' }}
                notMerge={true}
                onEvents={syncEvents(tempChartRef)}
              />
            </div>
          )}

          {/* 4. Cycle Charts - Every map item is a direct child */}
          {isCycleTelemetryVisible &&
            cycleChartFields.map((field, index) => (
              <div key={field} style={chartContainerStyle}>
                <ReactECharts
                  ref={chartRefs[index]}
                  option={getOption(field)}
                  style={{ height: '100%', width: '100%' }}
                  notMerge={true}
                  onEvents={syncEvents(chartRefs[index])}
                />
              </div>
            ))}
        </div>
      </div>
    </>
  );
}
